import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

export const spaLineItemInputSchema = z
  .object({
    productNumber: z
      .string()
      .trim()
      .describe(
        "Vendor catalog / model number. Required on a kept row; may be empty on a " +
          "flagged one (keptFlag false), which is how a source row that stated no " +
          "product code is recorded rather than silently discarded.",
      ),
    keptFlag: z
      .boolean()
      .optional()
      .describe(
        "True if the row survives cleanup and flows to margin calc. Defaults to true.",
      ),
    dropReason: z
      .string()
      .nullish()
      .describe(
        "Reason the row was dropped, e.g. blank_row, missing_catalog, deleted, expired, future_start, ecom_only. Ignored if not one of these values. " +
          "Nullable, not merely optional: a kept row states null here rather than " +
          "omitting the key — `mapEdi845ToSpa` emits it for every line and the " +
          "handler's `toDropReason` already reads null as 'not dropped'. Accepting " +
          "only string|undefined rejected every 845 at the step boundary, before " +
          "the handler ran.",
      ),
    rawData: z
      .record(z.string(), z.unknown())
      .optional()
      .describe(
        "All vendor-specific fields (description, price, multiplier, uom, per, validFrom, validTo, pgc, fixedValueDate, etc.).",
      ),
  })
  .passthrough()
  .refine((line) => line.keptFlag === false || line.productNumber.length > 0, {
    message:
      "productNumber is required unless the row is flagged (keptFlag false)",
    path: ["productNumber"],
  });

/**
 * One party the authorization names — a branch, an End Customer, a seller.
 *
 * Every role is accepted and none is interpreted: which `N101` role carries a
 * branch differs by supplier, and the vendor-code translation that resolves a
 * branch works from the code alone.
 */
const partyInputSchema = z.object({
  // Not `.min(1)`. The tokenizer reads `N101` as `el[1] ?? ""` precisely so a
  // malformed or reordered file never throws, and a role code this repo has
  // never seen is stored rather than rejected — so requiring one here would
  // reintroduce a hard failure at the persistence boundary. With `fail-fast`
  // batch semantics that failure takes down the whole chunk, losing every
  // contract in it over one blank element.
  roleCode: z
    .string()
    .describe(
      "The party's raw role code, `N101` on an 845 — e.g. EN (End Customer), " +
        "DS or BY (branch), SE (seller). Stored verbatim, and empty rather " +
        "than rejected when the sender stated none.",
    ),
  code: z
    .string()
    .optional()
    .describe(
      "The vendor's identifier for the party, `N104`. Omitted where the vendor " +
        "named the party without numbering it, as Schneider does for its End " +
        "Customers. Never normalized — leading zeros are significant.",
    ),
  name: z
    .string()
    .optional()
    .describe(
      "The party's printed name, `N102`. Decoration rather than identity: one " +
        "branch code can print under several spellings.",
    ),
  sequence: z
    .number()
    .int()
    .optional()
    .describe(
      "Position in document order, dense from zero. Defaults to the array index.",
    ),
});

export const upsertSpaStep = defineStepConfig({
  type: "persist_spa_from_extraction",
  name: "Upsert SPA",
  description:
    "Upsert a Pricing SPA record from structured extracted fields (e.g. from extract_document_content_v4). " +
    "When lineItems or lineItemsDocument is provided, also replaces all SpaLineItem rows for this SPA in the same transaction.",
  category: "Integration",
  libraryModule: "Pricing",

  inputSchema: z
    .object({
      record: z
        .record(z.string(), z.unknown())
        .describe(
          "Extracted SPA metadata object, usually the extractedFields output from extract_document_content_v4. Expected keys include spaNumber, revision, quoteType, startDate, endDate, currency, customerNumber, and division.",
        ),
      sourceDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
        "Original vendor SPA document. Its entityId is stored as sourceDocumentId on the Spa.",
      ),
      emailReceivedAt: z.iso
        .datetime({ offset: true })
        .nullish()
        .describe("Mailbox receipt timestamp from the email rule trigger"),
      lineItemsDocument: entityOfType(SystemEntity.CORE_DOCUMENT)
        .optional()
        .describe(
          "Normalized canonical CSV from the per-vendor cleanup + normalization nodes. When provided (and lineItems is not), all SpaLineItem rows for this SPA are replaced in the same transaction.",
        ),
      lineItemsJsonDocument: entityOfType(SystemEntity.CORE_DOCUMENT)
        .optional()
        .describe(
          "One contract's line items as a JSON document — the array `lineItems` would otherwise carry, written by map_845_to_spa. Read through the same builder as `lineItems`, so the rows stored are identical; it exists because a large authorization's lines exceed the workflow payload limit. Prefer this over `lineItems` for anything that can be large.",
        ),
      lineItems: z
        .array(spaLineItemInputSchema)
        .optional()
        .describe(
          "Structured line items to persist directly, as an alternative to lineItemsDocument. When provided, all SpaLineItem rows for this SPA are replaced with these rows (in array order) in the same transaction.",
        ),
      parties: z
        .array(partyInputSchema)
        .optional()
        .describe(
          "Every party the authorization names, verbatim and in document " +
            "order — one row per N1 segment, restatements included, never " +
            "collapsed on role plus code. When provided, all SpaParty rows " +
            "for this SPA are replaced with these rows in the same " +
            "transaction. Wire from Map 845 to SPA's parties output.",
        ),
      spaRequestId: z
        .string()
        .uuid()
        .optional()
        .describe("FK to the originating SpaRequest, when known."),
      spaSourceId: z
        .string()
        .uuid()
        .optional()
        .describe(
          "FK to the SpaSource (downloaded vendor file + portal metadata) this Spa was derived from, e.g. the spaSourceId output of upsert_spa_source.",
        ),
      vendor: z
        .string()
        .min(1)
        .describe("Vendor key, e.g. 'hoffman', 'rockwell'."),
    })
    .refine(
      (data) =>
        [
          data.lineItems,
          data.lineItemsDocument,
          data.lineItemsJsonDocument,
        ].filter((value) => value != null).length <= 1,
      {
        message:
          "Provide at most one of lineItems, lineItemsDocument or lineItemsJsonDocument, not several.",
        path: ["lineItems"],
      },
    ),

  outputSchema: z.object({
    spa: entityOfType(SystemEntity.PRICING_SPA).describe(
      "The persisted Spa record.",
    ),
    spaId: z.string().uuid().describe("The persisted Spa id."),
    created: z
      .boolean()
      .describe(
        "True when a new Spa row was created; false when an existing row was updated.",
      ),
    spaNumber: z.string().describe("The SPA agreement number."),
    revision: z
      .string()
      .nullable()
      .describe("Vendor revision/amendment number for this SPA."),
    customerNumber: z
      .string()
      .nullable()
      .describe("Customer account number from the SPA header."),
    startDate: z
      .string()
      .nullable()
      .describe("Contract start date (YYYY-MM-DD)."),
    endDate: z.string().nullable().describe("Contract end date (YYYY-MM-DD)."),
    quoteType: z
      .string()
      .nullable()
      .describe("SPA quote type (e.g. ShipAndDebit, StandardSpa)."),
    lineItemCount: z
      .number()
      .int()
      .nullable()
      .describe(
        "Total line item rows inserted. Null when neither lineItemsDocument nor lineItems was provided.",
      ),
    keptCount: z
      .number()
      .int()
      .nullable()
      .describe(
        "Line item rows with keptFlag=true. Null when neither lineItemsDocument nor lineItems was provided.",
      ),
    partyCount: z
      .number()
      .int()
      .nullable()
      .describe(
        "Party rows written — the branches and customers the authorization names. Null when no parties were provided.",
      ),
    pricingFlag: z
      .string()
      .nullable()
      .describe(
        "Why the authorization states no usable pricing, e.g. no_price_or_factor, or null when it prices something. A flagged record is stored in full but must not be read as a priced agreement.",
      ),
    contractStatus: z
      .string()
      .nullable()
      .describe(
        "CON03 as the vendor stated it — OC original, CM modified, EX expired — or null when the record states none. An EX contract is a termination; what it should do to the pricing it supersedes is not decided here, and nothing in this step acts on it.",
      ),
    purposeCode: z
      .string()
      .nullable()
      .describe(
        "BPA01 as the vendor stated it — 00 original, 04 change — or null when the record states none. An unpriced original is a contract being opened rather than one authorizing no discount; nothing in this step acts on that, it is recorded so a reader can tell the two apart.",
      ),
    ediGuidelineId: z
      .string()
      .nullable()
      .describe(
        "The vendor EDI guideline the record was read against, as stored. Null for a partner holding none, and for the portal flows, which are not read against a guideline at all.",
      ),
    interpretationFlags: z
      .array(z.string())
      .describe(
        "What the sending partner's guideline could not answer about this document, as stored. Empty for the portal flows and for a document a guideline answered in full. See Map 845 to SPA for the individual flags.",
      ),
  }),

  configSchema: z.object({
    productNumberColumn: z
      .string()
      .min(1)
      .default("productNumber")
      .describe("CSV column name that maps to productNumber."),
    keptFlagColumn: z
      .string()
      .min(1)
      .default("keptFlag")
      .describe('CSV column name for the kept/dropped flag ("true"/"false").'),
    dropReasonColumn: z
      .string()
      .min(1)
      .default("dropReason")
      .describe("CSV column name for the drop reason string."),
  }),

  emits: [] as const,
});

export type UpsertSpaConfig = z.infer<typeof upsertSpaStep.configSchema>;
